import React, { createContext, useContext, useState } from 'react';

export type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';

interface CurrencyRate {
  symbol: string;
  rate: number; // rate against 1 USD
  name: string;
}

export const EXCHANGE_RATES: Record<CurrencyCode, CurrencyRate> = {
  USD: { symbol: '$', rate: 1.00, name: 'US Dollar' },
  EUR: { symbol: '€', rate: 0.92, name: 'Euro' },
  GBP: { symbol: '£', rate: 0.78, name: 'British Pound' },
  CAD: { symbol: 'CA$', rate: 1.36, name: 'Canadian Dollar' },
  AUD: { symbol: 'AU$', rate: 1.52, name: 'Australian Dollar' },
};

export const EXCHANGE_TIMESTAMP = '2026-10-04T00:00:00Z';

interface CurrencyContextType {
  currency: CurrencyCode;
  setCurrency: (curr: CurrencyCode) => void;
  formatPrice: (cents: number) => string;
  displayPrice: (cents: number) => { formatted: string; amount: number; symbol: string; chargedUsd: string };
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    return (localStorage.getItem('gv_currency') as CurrencyCode) || 'USD';
  });

  const setCurrency = (c: CurrencyCode) => {
    localStorage.setItem('gv_currency', c);
    setCurrencyState(c);
  };

  const displayPrice = (cents: number) => {
    const rateInfo = EXCHANGE_RATES[currency] || EXCHANGE_RATES.USD;
    const usdAmount = cents / 100;
    const converted = usdAmount * rateInfo.rate;
    const formatted = `${rateInfo.symbol}${converted.toFixed(2)}`;
    const chargedUsd = `$${usdAmount.toFixed(2)} USD`;
    return { formatted, amount: converted, symbol: rateInfo.symbol, chargedUsd };
  };

  const formatPrice = (cents: number) => {
    return displayPrice(cents).formatted;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatPrice, displayPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within CurrencyProvider');
  return context;
};
